import { Controller } from "@hotwired/stimulus"

/**
 * OrgChart Controller - 조직도 컴포넌트 컨트롤러
 *
 * 기능:
 * - 노드 펼치기/접기 (collapse/expand)
 * - 노드 클릭 이벤트 처리
 * - Pan (드래그로 이동)
 * - Zoom (스크롤/핀치로 확대/축소)
 */
export default class extends Controller {
  static targets = ["node", "children", "toggleBtn", "toggleIcon", "viewport", "content"]
  static values = {
    pannable: { type: Boolean, default: false },
    zoomable: { type: Boolean, default: false },
    minZoom: { type: Number, default: 0.25 },
    maxZoom: { type: Number, default: 2 },
    zoomStep: { type: Number, default: 0.1 },
    multiSelect: { type: Boolean, default: false }
  }

  connect() {
    // Lucide 아이콘 초기화 (있는 경우)
    if (typeof lucide !== 'undefined') {
      lucide.createIcons()
    }

    // Pan/Zoom 초기화
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.isPanning = false
    this.startX = 0
    this.startY = 0

    // 선택된 노드 ID
    this.selectedNodeId = null

    if (this.pannableValue || this.zoomableValue) {
      this.initPanZoom()
    }
  }

  disconnect() {
    if (this.pannableValue || this.zoomableValue) {
      this.removePanZoomListeners()
    }
  }

  /**
   * Pan/Zoom 기능 초기화
   */
  initPanZoom() {
    const viewport = this.hasViewportTarget ? this.viewportTarget : this.element

    if (this.pannableValue) {
      viewport.addEventListener('mousedown', this.handleMouseDown.bind(this))
      viewport.addEventListener('mousemove', this.handleMouseMove.bind(this))
      viewport.addEventListener('mouseup', this.handleMouseUp.bind(this))
      viewport.addEventListener('mouseleave', this.handleMouseUp.bind(this))

      // Touch events
      viewport.addEventListener('touchstart', this.handleTouchStart.bind(this))
      viewport.addEventListener('touchmove', this.handleTouchMove.bind(this))
      viewport.addEventListener('touchend', this.handleTouchEnd.bind(this))
    }

    if (this.zoomableValue) {
      viewport.addEventListener('wheel', this.handleWheel.bind(this), { passive: false })
    }

    // 커서 스타일 설정
    if (this.pannableValue) {
      viewport.style.cursor = 'grab'
    }
  }

  /**
   * Pan/Zoom 리스너 제거
   */
  removePanZoomListeners() {
    const viewport = this.hasViewportTarget ? this.viewportTarget : this.element

    viewport.removeEventListener('mousedown', this.handleMouseDown)
    viewport.removeEventListener('mousemove', this.handleMouseMove)
    viewport.removeEventListener('mouseup', this.handleMouseUp)
    viewport.removeEventListener('mouseleave', this.handleMouseUp)
    viewport.removeEventListener('touchstart', this.handleTouchStart)
    viewport.removeEventListener('touchmove', this.handleTouchMove)
    viewport.removeEventListener('touchend', this.handleTouchEnd)
    viewport.removeEventListener('wheel', this.handleWheel)
  }

  /**
   * 트랜스폼 적용
   */
  applyTransform() {
    const content = this.hasContentTarget ? this.contentTarget : this.element.querySelector('.inline-block')
    if (content) {
      content.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`
      content.style.transformOrigin = '0 0'
    }
  }

  /**
   * 마우스 다운 핸들러 (Pan 시작)
   */
  handleMouseDown(event) {
    // 노드나 버튼 클릭은 무시
    if (event.target.closest('.org-node') || event.target.closest('button') || event.target.closest('a')) {
      return
    }

    this.isPanning = true
    this.startX = event.clientX - this.translateX
    this.startY = event.clientY - this.translateY

    const viewport = this.hasViewportTarget ? this.viewportTarget : this.element
    viewport.style.cursor = 'grabbing'
    event.preventDefault()
  }

  /**
   * 마우스 이동 핸들러 (Pan 중)
   */
  handleMouseMove(event) {
    if (!this.isPanning) return

    this.translateX = event.clientX - this.startX
    this.translateY = event.clientY - this.startY
    this.applyTransform()
  }

  /**
   * 마우스 업 핸들러 (Pan 종료)
   */
  handleMouseUp() {
    this.isPanning = false
    const viewport = this.hasViewportTarget ? this.viewportTarget : this.element
    if (this.pannableValue) {
      viewport.style.cursor = 'grab'
    }
  }

  /**
   * 터치 시작 핸들러
   */
  handleTouchStart(event) {
    if (event.touches.length === 1) {
      // 노드나 버튼 클릭은 무시
      if (event.target.closest('.org-node') || event.target.closest('button') || event.target.closest('a')) {
        return
      }

      this.isPanning = true
      this.startX = event.touches[0].clientX - this.translateX
      this.startY = event.touches[0].clientY - this.translateY
    } else if (event.touches.length === 2 && this.zoomableValue) {
      // 핀치 줌 시작
      this.initialPinchDistance = this.getPinchDistance(event.touches)
      this.initialScale = this.scale
    }
  }

  /**
   * 터치 이동 핸들러
   */
  handleTouchMove(event) {
    if (event.touches.length === 1 && this.isPanning) {
      this.translateX = event.touches[0].clientX - this.startX
      this.translateY = event.touches[0].clientY - this.startY
      this.applyTransform()
      event.preventDefault()
    } else if (event.touches.length === 2 && this.zoomableValue && this.initialPinchDistance) {
      // 핀치 줌
      const currentDistance = this.getPinchDistance(event.touches)
      const scaleFactor = currentDistance / this.initialPinchDistance
      this.scale = Math.min(Math.max(this.initialScale * scaleFactor, this.minZoomValue), this.maxZoomValue)
      this.applyTransform()
      event.preventDefault()
    }
  }

  /**
   * 터치 종료 핸들러
   */
  handleTouchEnd() {
    this.isPanning = false
    this.initialPinchDistance = null
  }

  /**
   * 핀치 거리 계산
   */
  getPinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * 휠 핸들러 (Zoom)
   */
  handleWheel(event) {
    event.preventDefault()

    const viewport = this.hasViewportTarget ? this.viewportTarget : this.element
    const rect = viewport.getBoundingClientRect()

    // 마우스 위치를 기준으로 줌
    const mouseX = event.clientX - rect.left
    const mouseY = event.clientY - rect.top

    const delta = event.deltaY > 0 ? -this.zoomStepValue : this.zoomStepValue
    const newScale = Math.min(Math.max(this.scale + delta, this.minZoomValue), this.maxZoomValue)

    if (newScale !== this.scale) {
      // 마우스 위치를 기준으로 줌 (줌 중심점 유지)
      const scaleRatio = newScale / this.scale
      this.translateX = mouseX - (mouseX - this.translateX) * scaleRatio
      this.translateY = mouseY - (mouseY - this.translateY) * scaleRatio
      this.scale = newScale

      this.applyTransform()
      this.dispatch("zoom", { detail: { scale: this.scale } })
    }
  }

  /**
   * 줌 인
   */
  zoomIn() {
    const newScale = Math.min(this.scale + this.zoomStepValue, this.maxZoomValue)
    if (newScale !== this.scale) {
      this.scale = newScale
      this.applyTransform()
      this.dispatch("zoom", { detail: { scale: this.scale } })
    }
  }

  /**
   * 줌 아웃
   */
  zoomOut() {
    const newScale = Math.max(this.scale - this.zoomStepValue, this.minZoomValue)
    if (newScale !== this.scale) {
      this.scale = newScale
      this.applyTransform()
      this.dispatch("zoom", { detail: { scale: this.scale } })
    }
  }

  /**
   * 줌 리셋
   */
  resetZoom() {
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.applyTransform()
    this.dispatch("zoom", { detail: { scale: this.scale } })
  }

  /**
   * 특정 줌 레벨로 설정
   */
  setZoom(scale) {
    this.scale = Math.min(Math.max(scale, this.minZoomValue), this.maxZoomValue)
    this.applyTransform()
    this.dispatch("zoom", { detail: { scale: this.scale } })
  }

  /**
   * 노드 펼치기/접기 토글
   */
  toggleCollapse(event) {
    event.stopPropagation()

    const nodeWrapper = event.target.closest('[data-org-chart-target="node"]')
    if (!nodeWrapper) return

    const childrenContainer = nodeWrapper.querySelector(':scope > [data-org-chart-target="children"]')
    const toggleBtn = event.target.closest('[data-org-chart-target="toggleBtn"]')
    const toggleIcon = toggleBtn?.querySelector('[data-org-chart-target="toggleIcon"]')

    if (childrenContainer) {
      const isCollapsed = childrenContainer.classList.contains('hidden')

      if (isCollapsed) {
        // 펼치기
        childrenContainer.classList.remove('hidden')
        if (toggleIcon) {
          toggleIcon.classList.remove('rotate-[-90deg]')
        }
        toggleBtn?.setAttribute('aria-label', '접기')
      } else {
        // 접기
        childrenContainer.classList.add('hidden')
        if (toggleIcon) {
          toggleIcon.classList.add('rotate-[-90deg]')
        }
        toggleBtn?.setAttribute('aria-label', '펼치기')
      }
    }
  }

  /**
   * 노드 클릭 핸들러
   * 커스텀 이벤트를 디스패치하여 부모 컴포넌트에서 처리할 수 있도록 함
   */
  handleNodeClick(event) {
    // 이벤트 버블링 방지 (중첩된 노드에서 상위로 전파 방지)
    event.stopPropagation()

    // 패닝 중이면 무시
    if (this.isPanning) return

    // 토글 버튼 클릭은 무시
    if (event.target.closest('[data-action*="toggleCollapse"]')) {
      return
    }

    const nodeWrapper = event.currentTarget.closest('[data-org-chart-target="node"]')
    const nodeId = nodeWrapper?.dataset.nodeId

    if (nodeId) {
      const nodeDepth = parseInt(nodeWrapper?.dataset.nodeDepth || '0', 10)

      // multiSelect 모드: 자동 single-select 동작 없이 이벤트만 dispatch
      // (visual 상태 관리는 호출 측에서 담당)
      if (this.multiSelectValue) {
        this.dispatch("nodeClick", {
          detail: { nodeId, nodeDepth },
          bubbles: true
        })
        return
      }

      // 같은 노드 클릭 시 선택 해제 (nodeClick 이벤트 발생 안함)
      if (this.selectedNodeId === nodeId) {
        this.selectNode(nodeId)  // nodeDeselect 이벤트 발생
        return
      }

      // 새 노드 선택
      this.selectNode(nodeId)
      this.dispatch("nodeClick", {
        detail: { nodeId, nodeDepth },
        bubbles: true
      })
    }
  }

  /**
   * 노드 선택
   * @param {string} nodeId - 선택할 노드 ID (null이면 선택 해제)
   */
  selectNode(nodeId) {
    // 이전 선택 해제
    if (this.selectedNodeId) {
      const prevNode = this.element.querySelector(`[data-node-id="${this.selectedNodeId}"]`)
      if (prevNode) {
        prevNode.classList.remove('org-node-selected')
      }
    }

    // 같은 노드를 다시 클릭하면 선택 해제
    if (this.selectedNodeId === nodeId) {
      this.selectedNodeId = null
      this.dispatch("nodeDeselect", { bubbles: true })
      return
    }

    // 새 노드 선택
    this.selectedNodeId = nodeId
    const newNode = this.element.querySelector(`[data-node-id="${nodeId}"]`)
    if (newNode) {
      newNode.classList.add('org-node-selected')
    }
  }

  /**
   * 선택 해제
   */
  deselectNode() {
    if (this.selectedNodeId) {
      const prevNode = this.element.querySelector(`[data-node-id="${this.selectedNodeId}"]`)
      if (prevNode) {
        prevNode.classList.remove('org-node-selected')
      }
      this.selectedNodeId = null
      this.dispatch("nodeDeselect", { bubbles: true })
    }
  }

  /**
   * 현재 선택된 노드 ID 반환
   */
  getSelectedNodeId() {
    return this.selectedNodeId
  }

  /**
   * 모든 노드 펼치기
   */
  expandAll() {
    this.element.querySelectorAll('[data-org-chart-target="children"].hidden').forEach(children => {
      children.classList.remove('hidden')
    })
    this.element.querySelectorAll('[data-org-chart-target="toggleIcon"]').forEach(icon => {
      icon.classList.remove('rotate-[-90deg]')
    })
    this.element.querySelectorAll('[data-org-chart-target="toggleBtn"]').forEach(btn => {
      btn.setAttribute('aria-label', '접기')
    })
  }

  /**
   * 모든 노드 접기
   */
  collapseAll() {
    this.element.querySelectorAll('[data-org-chart-target="children"]').forEach(children => {
      children.classList.add('hidden')
    })
    this.element.querySelectorAll('[data-org-chart-target="toggleIcon"]').forEach(icon => {
      icon.classList.add('rotate-[-90deg]')
    })
    this.element.querySelectorAll('[data-org-chart-target="toggleBtn"]').forEach(btn => {
      btn.setAttribute('aria-label', '펼치기')
    })
  }
}
